// src/components/WorkflowCreator/nodes/nodeDefinitions.js
// Definiciones de todos los tipos de nodos disponibles para workflows

// Simple UUID generator (crypto.randomUUID fallback)
const generateUUID = () => {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  // Fallback for older browsers
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
};

/**
 * Categorías de nodos para el panel lateral
 */
export const NODE_CATEGORIES = {
  TRIGGERS: {
    id: 'triggers',
    label: 'Triggers',
    description: 'Events that start the workflow',
    color: 'from-green-500 to-emerald-600',
    icon: 'Zap'
  },
  AI: {
    id: 'ai',
    label: 'AI & Assistants',
    description: 'AI-powered processing steps',
    color: 'from-purple-500 to-violet-600',
    icon: 'Bot'
  },
  INTEGRATIONS: {
    id: 'integrations',
    label: 'Integrations',
    description: 'External service connections',
    color: 'from-blue-500 to-cyan-600',
    icon: 'Plug'
  },
  NOTIFICATIONS: {
    id: 'notifications',
    label: 'Notifications',
    description: 'Send messages and alerts',
    color: 'from-orange-500 to-amber-600',
    icon: 'Bell'
  },
  DATA: {
    id: 'data',
    label: 'Data Processing',
    description: 'Transform and query data',
    color: 'from-teal-500 to-cyan-600',
    icon: 'Database'
  },
  CONTROL: {
    id: 'control',
    label: 'Control Flow',
    description: 'Logic and flow control',
    color: 'from-pink-500 to-rose-600',
    icon: 'GitBranch'
  },
  SYSTEM: {
    id: 'system',
    label: 'System',
    description: 'Logging and metrics',
    color: 'from-gray-500 to-slate-600',
    icon: 'Settings'
  }
};

/**
 * Definiciones de todos los tipos de nodos
 * Cada nodo incluye:
 * - Metadata para UI (icon, label, category, etc)
 * - Schema de parámetros requeridos/opcionales
 * - Función para generar datos iniciales
 */
export const NODE_DEFINITIONS = {
  // ============================================================================
  // TRIGGERS
  // ============================================================================
  shopify_trigger: {
    type: 'shopify_trigger',
    category: 'triggers',
    label: 'Shopify Trigger',
    description: 'Triggered by Shopify webhooks (orders, products, customers)',
    icon: 'ShoppingBag',
    color: 'from-green-500 to-emerald-600',
    isTrigger: true,
    parameters: {
      required: ['event_type', 'shop_domain'],
      optional: [],
      schema: {
        event_type: {
          type: 'select',
          label: 'Event Type',
          options: [
            { value: 'orders/create', label: 'Order Created' },
            { value: 'orders/updated', label: 'Order Updated' },
            { value: 'orders/paid', label: 'Order Paid' },
            { value: 'orders/fulfilled', label: 'Order Fulfilled' },
            { value: 'orders/cancelled', label: 'Order Cancelled' },
            { value: 'products/create', label: 'Product Created' },
            { value: 'products/update', label: 'Product Updated' },
            { value: 'customers/create', label: 'Customer Created' },
            { value: 'customers/update', label: 'Customer Updated' },
          ],
          default: 'orders/create'
        },
        shop_domain: {
          type: 'text',
          label: 'Shop Domain',
          placeholder: 'your-store.myshopify.com',
          required: true
        }
      }
    },
    generateTriggerConfig: (params) => ({
      trigger_type: 'shopify',
      configuration: {
        enabled: true,
        description: `Shopify ${params.event_type} trigger`,
        metadata: {},
        trigger_type: 'shopify',
        shop_domain: params.shop_domain,
        event_type: params.event_type,
        integration_id: params.integration_id || null
      }
    })
  },

  webhook_trigger: {
    type: 'webhook_trigger',
    category: 'triggers',
    label: 'Webhook Trigger',
    description: 'Triggered by incoming webhooks',
    icon: 'Webhook',
    color: 'from-blue-500 to-indigo-600',
    isTrigger: true,
    parameters: {
      required: [],
      optional: ['webhook_path', 'method'],
      schema: {
        webhook_path: {
          type: 'text',
          label: 'Webhook Path',
          placeholder: '/my-webhook'
        },
        method: {
          type: 'select',
          label: 'HTTP Method',
          options: [
            { value: 'POST', label: 'POST' },
            { value: 'GET', label: 'GET' },
            { value: 'PUT', label: 'PUT' }
          ],
          default: 'POST'
        }
      }
    },
    generateTriggerConfig: (params) => ({
      trigger_type: 'webhook',
      configuration: {
        enabled: true,
        webhook_path: params.webhook_path,
        method: params.method || 'POST'
      }
    })
  },

  schedule_trigger: {
    type: 'schedule_trigger',
    category: 'triggers',
    label: 'Schedule Trigger',
    description: 'Triggered on a schedule (cron)',
    icon: 'Clock',
    color: 'from-yellow-500 to-orange-600',
    isTrigger: true,
    parameters: {
      required: ['cron_expression'],
      optional: ['timezone'],
      schema: {
        cron_expression: {
          type: 'text',
          label: 'Cron Expression',
          placeholder: '0 9 * * *',
          required: true
        },
        timezone: {
          type: 'text',
          label: 'Timezone',
          placeholder: 'America/New_York',
          default: 'UTC'
        }
      }
    },
    generateTriggerConfig: (params) => ({
      trigger_type: 'schedule',
      configuration: {
        enabled: true,
        cron_expression: params.cron_expression,
        timezone: params.timezone || 'UTC'
      }
    })
  },

  whatsapp_trigger: {
    type: 'whatsapp_trigger',
    category: 'triggers',
    label: 'WhatsApp Trigger',
    description: 'Triggered by WhatsApp messages and events',
    icon: 'MessageCircle',
    color: 'from-green-500 to-emerald-600',
    isTrigger: true,
    parameters: {
      required: ['integration_id', 'business_phone_number_id', 'event_types'],
      optional: [],
      schema: {
        integration_id: {
          type: 'whatsapp_integration_select',
          label: 'WhatsApp Integration',
          required: true,
          description: 'Select the WhatsApp Business account to listen to'
        },
        business_phone_number_id: {
          type: 'text',
          label: 'Phone Number ID',
          placeholder: '387311654443856',
          required: true,
          description: 'WhatsApp Business Phone Number ID from Meta'
        },
        event_types: {
          type: 'multi_select',
          label: 'Event Types',
          required: true,
          options: [
            { value: 'messages', label: 'Messages (Received)', default: true },
            { value: 'message_deliveries', label: 'Message Deliveries' },
            { value: 'message_reads', label: 'Message Reads' },
            { value: 'message_reactions', label: 'Message Reactions' },
            { value: 'contacts', label: 'Contacts Update' },
            { value: 'account_update', label: 'Account Update' },
            { value: 'phone_number_name_update', label: 'Phone Number Name Update' },
            { value: 'phone_number_quality_update', label: 'Phone Number Quality Update' }
          ],
          default: ['messages'],
          description: 'Types of WhatsApp events to trigger this workflow'
        }
      }
    },
    generateTriggerConfig: (params) => ({
      trigger_type: 'whatsapp',
      configuration: {
        enabled: true,
        description: 'WhatsApp trigger for selected events',
        trigger_type: 'whatsapp',
        integration_type: 'whatsapp',
        path: '/whatsapp/webhook',
        method: 'POST',
        content_type: 'application/json',
        business_phone_number_id: params.business_phone_number_id,
        event_types: params.event_types || ['messages'],
        integration_id: params.integration_id,
        authentication_type: 'webhook_signature'
      }
    })
  },

  // ============================================================================
  // AI STEPS
  // ============================================================================
  assistant_task: {
    type: 'assistant_task',
    category: 'ai',
    label: 'AI Assistant Task',
    description: 'Process with an AI assistant',
    icon: 'Bot',
    color: 'from-purple-500 to-violet-600',
    parameters: {
      required: ['assistant_id', 'message_template'],
      optional: ['chat_mode', 'temperature', 'max_tokens', 'context_variables'],
      schema: {
        assistant_id: {
          type: 'assistant_select',
          label: 'Assistant',
          required: true
        },
        message_template: {
          type: 'textarea',
          label: 'Message Template',
          placeholder: 'Analyze this order: {{input:webhook_payload}}',
          required: true,
          rows: 4
        },
        chat_mode: {
          type: 'select',
          label: 'Chat Mode',
          options: [
            { value: 'one_shot', label: 'One Shot (single response)' },
            { value: 'conversation', label: 'Conversation (maintains context)' }
          ],
          default: 'one_shot'
        },
        temperature: {
          type: 'number',
          label: 'Temperature',
          min: 0,
          max: 2,
          step: 0.1,
          default: 0.7
        },
        max_tokens: {
          type: 'number',
          label: 'Max Tokens',
          min: 1,
          max: 4000,
          default: 2000
        }
      }
    },
    generateStepParams: (params) => ({
      step_type: 'assistant_task',
      assistant_id: params.assistant_id,
      message_template: params.message_template,
      chat_mode: params.chat_mode || 'one_shot',
      temperature: params.temperature ?? 0.7,
      max_tokens: params.max_tokens ?? 2000
    })
  },

  llm_call: {
    type: 'llm_call',
    category: 'ai',
    label: 'Direct LLM Call',
    description: 'Call an LLM directly without assistant',
    icon: 'Sparkles',
    color: 'from-violet-500 to-purple-600',
    parameters: {
      required: ['provider', 'model', 'prompt'],
      optional: ['temperature', 'max_tokens', 'system_prompt'],
      schema: {
        provider: {
          type: 'select',
          label: 'Provider',
          options: [
            { value: 'openai', label: 'OpenAI' },
            { value: 'anthropic', label: 'Anthropic' },
            { value: 'google', label: 'Google AI' }
          ],
          required: true
        },
        model: {
          type: 'text',
          label: 'Model',
          placeholder: 'gpt-4o-mini',
          required: true
        },
        prompt: {
          type: 'textarea',
          label: 'Prompt',
          placeholder: 'Your prompt here...',
          required: true,
          rows: 4
        },
        system_prompt: {
          type: 'textarea',
          label: 'System Prompt',
          placeholder: 'Optional system instructions...',
          rows: 2
        },
        temperature: {
          type: 'number',
          label: 'Temperature',
          min: 0,
          max: 2,
          step: 0.1,
          default: 0.7
        },
        max_tokens: {
          type: 'number',
          label: 'Max Tokens',
          min: 1,
          max: 4000,
          default: 2000
        }
      }
    },
    generateStepParams: (params) => ({
      step_type: 'llm_call',
      provider: params.provider,
      model: params.model,
      prompt: params.prompt,
      system_prompt: params.system_prompt || null,
      temperature: params.temperature ?? 0.7,
      max_tokens: params.max_tokens ?? 2000
    })
  },

  // ============================================================================
  // INTEGRATIONS
  // ============================================================================
  shopify_update_order: {
    type: 'shopify_update_order',
    category: 'integrations',
    label: 'Update Shopify Order',
    description: 'Update an order in Shopify',
    icon: 'ShoppingBag',
    color: 'from-green-500 to-emerald-600',
    parameters: {
      required: ['order_id'],
      optional: ['shop_domain', 'tags', 'note', 'email', 'phone'],
      schema: {
        order_id: {
          type: 'text',
          label: 'Order ID',
          placeholder: '{{input:webhook_payload.id}}',
          required: true
        },
        shop_domain: {
          type: 'text',
          label: 'Shop Domain',
          placeholder: 'Auto-detected if not provided'
        },
        tags: {
          type: 'text',
          label: 'Tags',
          placeholder: '{{context:step_name.result_data.tags}} or comma-separated'
        },
        note: {
          type: 'textarea',
          label: 'Order Note',
          placeholder: 'Note to add to the order',
          rows: 2
        },
        email: {
          type: 'text',
          label: 'Customer Email',
          placeholder: 'Optional email update'
        },
        phone: {
          type: 'text',
          label: 'Customer Phone',
          placeholder: 'Optional phone update'
        }
      }
    },
    generateStepParams: (params) => ({
      step_type: 'shopify_update_order',
      order_id: params.order_id,
      shop_domain: params.shop_domain || null,
      tags: params.tags || null,
      note: params.note || null,
      email: params.email || null,
      phone: params.phone || null,
      use_template_variables: true
    })
  },

  api_call: {
    type: 'api_call',
    category: 'integrations',
    label: 'API Call',
    description: 'Make an HTTP request to external API',
    icon: 'Globe',
    color: 'from-blue-500 to-cyan-600',
    parameters: {
      required: ['url'],
      optional: ['method', 'headers', 'body', 'timeout'],
      schema: {
        url: {
          type: 'text',
          label: 'URL',
          placeholder: 'https://api.example.com/endpoint',
          required: true
        },
        method: {
          type: 'select',
          label: 'Method',
          options: [
            { value: 'GET', label: 'GET' },
            { value: 'POST', label: 'POST' },
            { value: 'PUT', label: 'PUT' },
            { value: 'PATCH', label: 'PATCH' },
            { value: 'DELETE', label: 'DELETE' }
          ],
          default: 'GET'
        },
        headers: {
          type: 'json',
          label: 'Headers',
          placeholder: '{"Authorization": "Bearer xxx"}',
          rows: 3
        },
        body: {
          type: 'json',
          label: 'Body',
          placeholder: '{"key": "value"}',
          rows: 4
        },
        timeout: {
          type: 'number',
          label: 'Timeout (seconds)',
          min: 1,
          max: 300,
          default: 30
        }
      }
    },
    generateStepParams: (params) => {
      let headers = null;
      let body = null;
      try {
        headers = params.headers ? JSON.parse(params.headers) : null;
      } catch (e) {}
      try {
        body = params.body ? JSON.parse(params.body) : null;
      } catch (e) {}
      return {
        step_type: 'api_call',
        url: params.url,
        method: params.method || 'GET',
        headers,
        body,
        timeout: params.timeout ?? 30
      };
    }
  },

  webhook: {
    type: 'webhook',
    category: 'integrations',
    label: 'Send Webhook',
    description: 'Send data to a webhook URL',
    icon: 'Send',
    color: 'from-indigo-500 to-blue-600',
    parameters: {
      required: ['url'],
      optional: ['method', 'headers', 'payload_template'],
      schema: {
        url: {
          type: 'text',
          label: 'Webhook URL',
          placeholder: 'https://hooks.example.com/webhook',
          required: true
        },
        method: {
          type: 'select',
          label: 'Method',
          options: [
            { value: 'POST', label: 'POST' },
            { value: 'PUT', label: 'PUT' }
          ],
          default: 'POST'
        },
        headers: {
          type: 'json',
          label: 'Headers',
          placeholder: '{"Content-Type": "application/json"}'
        },
        payload_template: {
          type: 'textarea',
          label: 'Payload Template',
          placeholder: 'Template with {{context:step_name.result}}',
          rows: 4
        }
      }
    },
    generateStepParams: (params) => {
      let headers = null;
      try {
        headers = params.headers ? JSON.parse(params.headers) : null;
      } catch (e) {}
      return {
        step_type: 'webhook',
        url: params.url,
        method: params.method || 'POST',
        headers,
        payload_template: params.payload_template || null
      };
    }
  },

  // ============================================================================
  // NOTIFICATIONS
  // ============================================================================
  whatsapp_notification: {
    type: 'whatsapp_notification',
    category: 'notifications',
    label: 'WhatsApp Message',
    description: 'Send a WhatsApp message',
    icon: 'MessageCircle',
    color: 'from-green-500 to-emerald-600',
    parameters: {
      required: ['phone_number_id', 'recipient', 'message_content'],
      optional: ['integration_id', 'message_type', 'template_name', 'template_language', 'template_parameters'],
      schema: {
        integration_id: {
          type: 'integration_select',
          label: 'WhatsApp Integration (Optional)',
          integration_type: 'meta',
          required: false
        },
        phone_number_id: {
          type: 'text',
          label: 'Phone Number ID',
          placeholder: 'WhatsApp Business Phone Number ID',
          required: true
        },
        recipient: {
          type: 'text',
          label: 'Recipient',
          placeholder: '+1234567890 or {{input:webhook_payload.customer.phone}}',
          required: true
        },
        message_type: {
          type: 'select',
          label: 'Message Type',
          options: [
            { value: 'text', label: 'Text Message' },
            { value: 'template', label: 'Template Message' },
            { value: 'image', label: 'Image' },
            { value: 'document', label: 'Document' }
          ],
          default: 'text'
        },
        message_content: {
          type: 'textarea',
          label: 'Message Content',
          placeholder: 'Hello {{input:webhook_payload.customer.first_name}}!',
          required: true,
          rows: 3
        },
        template_name: {
          type: 'text',
          label: 'Template Name',
          placeholder: 'order_confirmation (required for template type)'
        },
        template_language: {
          type: 'text',
          label: 'Template Language',
          placeholder: 'en',
          default: 'en'
        },
        template_parameters: {
          type: 'json',
          label: 'Template Parameters',
          placeholder: '[{"name": "customer_name", "value": "{{input:...}}"}]',
          rows: 3
        }
      }
    },
    generateStepParams: (params) => {
      let parsedTemplateParams = null;
      if (params.template_parameters) {
        try {
          parsedTemplateParams = JSON.parse(params.template_parameters);
        } catch (e) {
          console.error('Invalid JSON in template_parameters:', e);
          parsedTemplateParams = null;
        }
      }
      
      return {
        step_type: 'whatsapp_notification',
        phone_number_id: params.phone_number_id,
        recipient: params.recipient,
        message_content: params.message_content,
        message_type: params.message_type || 'text',
        template_name: params.template_name || null,
        template_language: params.template_language || 'en',
        template_parameters: parsedTemplateParams
      };
    }
  },

  slack_notification: {
    type: 'slack_notification',
    category: 'notifications',
    label: 'Slack Message',
    description: 'Send a Slack message',
    icon: 'Hash',
    color: 'from-purple-500 to-fuchsia-600',
    parameters: {
      required: ['channel', 'text'],
      optional: ['thread_ts', 'blocks'],
      schema: {
        channel: {
          type: 'text',
          label: 'Channel',
          placeholder: '#general or channel ID',
          required: true
        },
        text: {
          type: 'textarea',
          label: 'Message Text',
          placeholder: 'Your message here...',
          required: true,
          rows: 3
        },
        thread_ts: {
          type: 'text',
          label: 'Thread Timestamp',
          placeholder: 'Optional - for thread replies'
        },
        blocks: {
          type: 'json',
          label: 'Block Kit Blocks',
          placeholder: 'Optional Slack Block Kit JSON',
          rows: 4
        }
      }
    },
    generateStepParams: (params) => {
      let blocks = null;
      try {
        blocks = params.blocks ? JSON.parse(params.blocks) : null;
      } catch (e) {}
      return {
        step_type: 'slack_notification',
        channel: params.channel,
        text: params.text,
        thread_ts: params.thread_ts || null,
        blocks,
        unfurl_links: true,
        unfurl_media: true
      };
    }
  },

  discord_notification: {
    type: 'discord_notification',
    category: 'notifications',
    label: 'Discord Message',
    description: 'Send a Discord message',
    icon: 'MessageSquare',
    color: 'from-indigo-500 to-violet-600',
    parameters: {
      required: ['channel_id'],
      optional: ['content', 'embeds', 'username', 'avatar_url'],
      schema: {
        channel_id: {
          type: 'text',
          label: 'Channel ID',
          placeholder: 'Discord channel ID',
          required: true
        },
        content: {
          type: 'textarea',
          label: 'Message Content',
          placeholder: 'Your message here...',
          rows: 3
        },
        embeds: {
          type: 'json',
          label: 'Embeds',
          placeholder: 'Discord embed JSON array',
          rows: 4
        },
        username: {
          type: 'text',
          label: 'Bot Username Override',
          placeholder: 'Optional custom username'
        },
        avatar_url: {
          type: 'text',
          label: 'Avatar URL Override',
          placeholder: 'Optional custom avatar'
        }
      }
    },
    generateStepParams: (params) => {
      let embeds = null;
      try {
        embeds = params.embeds ? JSON.parse(params.embeds) : null;
      } catch (e) {}
      return {
        step_type: 'discord_notification',
        channel_id: params.channel_id,
        content: params.content || null,
        embeds,
        username: params.username || null,
        avatar_url: params.avatar_url || null,
        tts: false
      };
    }
  },

  gmail: {
    type: 'gmail',
    category: 'notifications',
    label: 'Send Email (Gmail)',
    description: 'Send an email via Gmail',
    icon: 'Mail',
    color: 'from-red-500 to-rose-600',
    parameters: {
      required: ['to', 'subject'],
      optional: ['cc', 'bcc', 'body_text', 'body_html'],
      schema: {
        to: {
          type: 'text',
          label: 'To',
          placeholder: 'recipient@example.com (comma-separated for multiple)',
          required: true
        },
        subject: {
          type: 'text',
          label: 'Subject',
          placeholder: 'Email subject',
          required: true
        },
        cc: {
          type: 'text',
          label: 'CC',
          placeholder: 'cc@example.com'
        },
        bcc: {
          type: 'text',
          label: 'BCC',
          placeholder: 'bcc@example.com'
        },
        body_text: {
          type: 'textarea',
          label: 'Body (Text)',
          placeholder: 'Plain text email body',
          rows: 4
        },
        body_html: {
          type: 'textarea',
          label: 'Body (HTML)',
          placeholder: '<h1>HTML email body</h1>',
          rows: 4
        }
      }
    },
    generateStepParams: (params) => ({
      step_type: 'gmail',
      to: params.to.split(',').map(e => e.trim()),
      subject: params.subject,
      cc: params.cc ? params.cc.split(',').map(e => e.trim()) : null,
      bcc: params.bcc ? params.bcc.split(',').map(e => e.trim()) : null,
      body_text: params.body_text || null,
      body_html: params.body_html || null
    })
  },

  // ============================================================================
  // DATA PROCESSING
  // ============================================================================
  data_transform: {
    type: 'data_transform',
    category: 'data',
    label: 'Data Transform',
    description: 'Transform and manipulate data',
    icon: 'Shuffle',
    color: 'from-teal-500 to-cyan-600',
    parameters: {
      required: ['transformations'],
      optional: [],
      schema: {
        transformations: {
          type: 'json',
          label: 'Transformations',
          placeholder: `[
  {"action": "rename", "source": "old_field", "target": "new_field"},
  {"action": "format", "source": "name", "target": "name", "format_type": "uppercase"}
]`,
          required: true,
          rows: 6
        }
      }
    },
    generateStepParams: (params) => {
      let transformations = null;
      try {
        transformations = JSON.parse(params.transformations);
      } catch (e) {}
      return {
        step_type: 'data_transform',
        transformations
      };
    }
  },

  database_query: {
    type: 'database_query',
    category: 'data',
    label: 'Database Query',
    description: 'Execute a database query',
    icon: 'Database',
    color: 'from-emerald-500 to-teal-600',
    parameters: {
      required: ['connection_id', 'query'],
      optional: ['query_type', 'timeout', 'parameters'],
      schema: {
        connection_id: {
          type: 'text',
          label: 'Connection ID',
          placeholder: 'Database connection UUID',
          required: true
        },
        query: {
          type: 'textarea',
          label: 'SQL Query',
          placeholder: 'SELECT * FROM orders WHERE id = :order_id',
          required: true,
          rows: 4
        },
        query_type: {
          type: 'select',
          label: 'Query Type',
          options: [
            { value: 'select', label: 'SELECT' },
            { value: 'insert', label: 'INSERT' },
            { value: 'update', label: 'UPDATE' },
            { value: 'delete', label: 'DELETE' }
          ],
          default: 'select'
        },
        timeout: {
          type: 'number',
          label: 'Timeout (seconds)',
          min: 1,
          max: 300,
          default: 30
        },
        parameters: {
          type: 'json',
          label: 'Query Parameters',
          placeholder: '{"order_id": "{{input:webhook_payload.id}}"}',
          rows: 3
        }
      }
    },
    generateStepParams: (params) => {
      let parameters = null;
      try {
        parameters = params.parameters ? JSON.parse(params.parameters) : null;
      } catch (e) {}
      return {
        step_type: 'database_query',
        connection_id: params.connection_id,
        query: params.query,
        query_type: params.query_type || 'select',
        timeout: params.timeout ?? 30,
        parameters
      };
    }
  },

  // ============================================================================
  // CONTROL FLOW
  // ============================================================================
  condition: {
    type: 'condition',
    category: 'control',
    label: 'Condition (If/Else)',
    description: 'Branch based on conditions with multiple operators',
    icon: 'GitBranch',
    color: 'from-pink-500 to-rose-600',
    parameters: {
      required: ['conditions'],
      optional: ['logic', 'true_branch_action', 'false_branch_action', 'if_description', 'else_description'],
      schema: {
        conditions: {
          type: 'array',
          label: 'Conditions',
          itemSchema: {
            variable: {
              type: 'text',
              label: 'Variable',
              placeholder: '{{context:step_name.result}}',
              required: true
            },
            operator: {
              type: 'select',
              label: 'Operator',
              options: [
                { value: 'equals', label: 'Equals (==)' },
                { value: 'not_equals', label: 'Not Equals (!=)' },
                { value: 'contains', label: 'Contains' },
                { value: 'not_contains', label: 'Does Not Contain' },
                { value: 'starts_with', label: 'Starts With' },
                { value: 'ends_with', label: 'Ends With' },
                { value: 'greater_than', label: 'Greater Than (>)' },
                { value: 'less_than', label: 'Less Than (<)' },
                { value: 'greater_or_equal', label: 'Greater or Equal (>=)' },
                { value: 'less_or_equal', label: 'Less or Equal (<=)' },
                { value: 'is_empty', label: 'Is Empty' },
                { value: 'is_not_empty', label: 'Is Not Empty' }
              ],
              default: 'equals',
              required: true
            },
            value: {
              type: 'text',
              label: 'Value',
              placeholder: 'Expected value',
              required: false
            }
          },
          minItems: 1,
          addButtonLabel: '+ Add Condition'
        },
        logic: {
          type: 'select',
          label: 'Logic',
          options: [
            { value: 'AND', label: 'AND - All conditions must be true' },
            { value: 'OR', label: 'OR - At least one condition must be true' }
          ],
          default: 'AND',
          description: 'How to combine multiple conditions'
        },
        if_description: {
          type: 'text',
          label: 'If True - Description',
          placeholder: 'Describe what happens when condition is true'
        },
        else_description: {
          type: 'text',
          label: 'If False - Description',
          placeholder: 'Describe what happens when condition is false'
        },
        true_branch_action: {
          type: 'select',
          label: 'If True - Action',
          options: [
            { value: 'continue', label: 'Continue to next step' },
            { value: 'skip_to_step', label: 'Skip to specific step' },
            { value: 'complete', label: 'Complete workflow' }
          ],
          default: 'continue'
        },
        false_branch_action: {
          type: 'select',
          label: 'If False - Action',
          options: [
            { value: 'continue', label: 'Continue to next step' },
            { value: 'skip_to_step', label: 'Skip to specific step' },
            { value: 'fail', label: 'Fail workflow' },
            { value: 'complete', label: 'Complete workflow' }
          ],
          default: 'continue'
        }
      }
    },
    generateStepParams: (params) => ({
      step_type: 'condition',
      condition: {
        conditions: params.conditions || [],
        logic: params.logic || 'AND'
      },
      if_description: params.if_description || '',
      else_description: params.else_description || '',
      true_branch_action: params.true_branch_action || 'continue',
      false_branch_action: params.false_branch_action || 'continue',
      true_branch_target_step: null,
      false_branch_target_step: null
    })
  },

  delay: {
    type: 'delay',
    category: 'control',
    label: 'Delay',
    description: 'Wait for a specified duration',
    icon: 'Timer',
    color: 'from-amber-500 to-orange-600',
    parameters: {
      required: ['duration_seconds'],
      optional: [],
      schema: {
        duration_seconds: {
          type: 'number',
          label: 'Duration (seconds)',
          min: 1,
          max: 86400,
          default: 60,
          required: true
        }
      }
    },
    generateStepParams: (params) => ({
      step_type: 'delay',
      duration_seconds: params.duration_seconds
    })
  },

  wait: {
    type: 'wait',
    category: 'control',
    label: 'Wait',
    description: 'Wait for condition, webhook, or user input',
    icon: 'Hourglass',
    color: 'from-yellow-500 to-amber-600',
    parameters: {
      required: ['wait_type'],
      optional: ['timeout_seconds', 'timeout_action'],
      schema: {
        wait_type: {
          type: 'select',
          label: 'Wait Type',
          options: [
            { value: 'duration', label: 'Duration' },
            { value: 'condition', label: 'Condition' },
            { value: 'webhook', label: 'Webhook' },
            { value: 'user_input', label: 'User Input' }
          ],
          required: true,
          default: 'duration'
        },
        timeout_seconds: {
          type: 'number',
          label: 'Timeout (seconds)',
          min: 1,
          max: 86400,
          default: 300
        },
        timeout_action: {
          type: 'select',
          label: 'Timeout Action',
          options: [
            { value: 'fail', label: 'Fail' },
            { value: 'continue', label: 'Continue' },
            { value: 'retry', label: 'Retry' }
          ],
          default: 'fail'
        }
      }
    },
    generateStepParams: (params) => ({
      step_type: 'wait',
      wait_type: params.wait_type,
      timeout_seconds: params.timeout_seconds ?? 300,
      timeout_action: params.timeout_action || 'fail'
    })
  },

  error_handler: {
    type: 'error_handler',
    category: 'control',
    label: 'Error Handler',
    description: 'Handle errors in the workflow',
    icon: 'AlertTriangle',
    color: 'from-red-500 to-rose-600',
    parameters: {
      required: ['error_types', 'action'],
      optional: ['retry_count'],
      schema: {
        error_types: {
          type: 'text',
          label: 'Error Types',
          placeholder: 'timeout,api_error,validation_error',
          required: true
        },
        action: {
          type: 'select',
          label: 'Action',
          options: [
            { value: 'retry', label: 'Retry' },
            { value: 'skip', label: 'Skip' },
            { value: 'fail', label: 'Fail' },
            { value: 'custom', label: 'Custom Handler' }
          ],
          required: true,
          default: 'retry'
        },
        retry_count: {
          type: 'number',
          label: 'Retry Count',
          min: 1,
          max: 10,
          default: 3
        }
      }
    },
    generateStepParams: (params) => ({
      step_type: 'error_handler',
      error_types: params.error_types.split(',').map(e => e.trim()),
      action: params.action,
      retry_count: params.action === 'retry' ? (params.retry_count ?? 3) : null
    })
  },

  // ============================================================================
  // SYSTEM
  // ============================================================================
  log: {
    type: 'log',
    category: 'system',
    label: 'Log',
    description: 'Log a message',
    icon: 'FileText',
    color: 'from-gray-500 to-slate-600',
    parameters: {
      required: ['message'],
      optional: ['level', 'logger_name'],
      schema: {
        message: {
          type: 'textarea',
          label: 'Message',
          placeholder: 'Order {{input:webhook_payload.id}} processed successfully',
          required: true,
          rows: 2
        },
        level: {
          type: 'select',
          label: 'Log Level',
          options: [
            { value: 'debug', label: 'Debug' },
            { value: 'info', label: 'Info' },
            { value: 'warning', label: 'Warning' },
            { value: 'error', label: 'Error' },
            { value: 'critical', label: 'Critical' }
          ],
          default: 'info'
        },
        logger_name: {
          type: 'text',
          label: 'Logger Name',
          placeholder: 'Optional custom logger name'
        }
      }
    },
    generateStepParams: (params) => ({
      step_type: 'log',
      message: params.message,
      level: params.level || 'info',
      logger_name: params.logger_name || null
    })
  }
};

/**
 * Helper para crear un nuevo nodo con ID temporal
 */
export const createNodeInstance = (nodeType, position = { x: 0, y: 0 }) => {
  const definition = NODE_DEFINITIONS[nodeType];
  if (!definition) {
    throw new Error(`Unknown node type: ${nodeType}`);
  }

  const tempId = generateUUID();
  
  return {
    id: tempId,
    type: nodeType,
    position,
    data: {
      ...definition,
      nodeId: tempId,
      params: {},
      isConfigured: false
    }
  };
};

/**
 * Helper para obtener nodos por categoría
 */
export const getNodesByCategory = (category) => {
  return Object.entries(NODE_DEFINITIONS)
    .filter(([_, def]) => def.category === category)
    .map(([type, def]) => ({ type, ...def }));
};

/**
 * Exportar lista de todos los tipos de nodos
 */
export const getAllNodeTypes = () => {
  return Object.keys(NODE_DEFINITIONS);
};
